import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import GameSliderContainer from 'components/GameSliderContainer'
import GetSliderGames from './GetSliderGames'
import Loader from 'components/Loader'

/**
 * CategorisedGamesGrid component displays a grid of game sliders based on subcategories.
 * It optionally limits the number of subcategories shown when rendered on the homepage.
 *
 * @component
 *
 * @param {Object} props
 * @param {Object} props.categorySlug - The parent category object for the games.
 * @param {boolean} props.isHomePage - Flag to determine if the component is rendered on the homepage.
 * @param {Function} props.navigate - Function to handle navigation (e.g., React Navigation or router).
 * @param {Object} props.gameCategory - Object containing information about the game category.
 * @param {Object} props.languageData - Localized text data for UI display.
 * @param {Function} props.changeMenuTab - Callback to change the currently selected menu/tab.
 * @param {Function} props.toggleFavorite - Function to toggle a game as favorite or not.
 * @param {Object} props.gameSubCategory - Object containing game subcategory data, expected to have a `rows` array.
 * @param {string} props.selectedLanguageCode - Language code for the currently selected language (e.g., "en", "fr").
 * @param {boolean} [props.subCatLoading=false] - Whether the subcategories are currently loading.
 *
 * @returns {JSX.Element} A container with sliders of categorized games.
 *
 * @example
 * <CategorisedGamesGrid
 *   categorySlug={categorySlug}
 *   isHomePage={true}
 *   navigate={navigate}
 *   gameCategory={gameCategory}
 *   languageData={languageData}
 *   changeMenuTab={changeMenuTab}
 *   toggleFavorite={toggleFavorite}
 *   gameSubCategory={gameSubCategory}
 *   selectedLanguageCode="en"
 *   subCatLoading={false}
 * />
 */
const CategorisedGamesGrid = ({
  categorySlug,
  isHomePage,
  navigate,
  gameCategory,
  languageData,
  // changeMenuTab,
  toggleFavorite,
  gameSubCategory,
  selectedLanguageCode,
  subCatLoading = false,
  favoriteGamesList,
  setSelectedFilter
}) => {
  const uiConfig = useSelector((state) => state?.uiConfig?.general)
  const { homePageSliderVariant, homeGameSubCategoryLimit } = uiConfig || {}

  const gameCategoryRows = useMemo(() => {
    // If it's the home page and there's a limit on the number of game subcategories to display,
    // slice the gameSubCategory rows to only include up to that limit.
    if (isHomePage && homeGameSubCategoryLimit) {
      return gameSubCategory?.rows?.slice(0, homeGameSubCategoryLimit)
    }

    // Otherwise, return all rows in gameSubCategory.
    return gameSubCategory?.rows
  }, [
    gameSubCategory?.rows,
    isHomePage,
    homeGameSubCategoryLimit
  ])

  return (
    <GameSliderContainer>
      {subCatLoading
        ? <Loader />
        : gameCategoryRows?.map?.((data, index) => (
          <GetSliderGames
            variant={isHomePage && homePageSliderVariant}
            categorySlug={categorySlug}
            games={data?.TenantGames}
            name={data?.name}
            slug={data?.slug}
            isLabeled={data?.isLabelledCategory}
            labelIconUrl={data?.labelIconUrl}
            imageUrl={data?.thumbnailUrl}
            id={data?.id}
            key={`${data?.id}`}
            selectedLanguageCode={selectedLanguageCode}
            navigate={navigate}
            // changeMenuTab={changeMenuTab}
            gameCategory={gameCategory}
            languageData={languageData}
            toggleFavorite={toggleFavorite}
            favoriteGamesList={favoriteGamesList}
            setSelectedFilter={setSelectedFilter}
          />
        ))}
    </GameSliderContainer>
  )
}

export default React.memo(CategorisedGamesGrid)
